import type { ReactElement } from 'react'
import styled from 'styled-components'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/enlayout'
import { Head } from '@/components/enlayouts/Head'
import { Layout } from '@/components/enlayouts/Ir'
import { BulletedList } from '@/components/enlayouts/Ir/BulletedList'
import { AHeading, BHeading } from '@/components/enlayouts/Ir/Heading'
import { Paragraph } from '@/components/enlayouts/Ir/Paragraph'

export const DisclosurePage = () => {
  return (
    <Layout>
      <SectionTitle
        color="normal"
        enTitleText="Disclosure / IR Policy"
        subTitleText=""
      />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <section>
        <AHeading>Disclosure Policy</AHeading>
        <Paragraph>
          TRIAL Group aims to &quot;Revolutionize the retail industry with technology&quot; through the combination of Distribution and IT.<br/>
          We embrace the purpose &quot;Enrich every aspect of people&apos;s lives through all we do.&quot; In pursuit of this, we strive for prompt, accurate, and fair disclosure, engaging in constructive dialogue with all stakeholders.<br/>
          Through this ongoing commitment, we seek to enhance transparency in management, fulfill corporate social responsibilities, 
          and earn trust. We disseminate this fundamental policy internally and externally, establishing the following Disclosure 
          Policy to consistently implement our disclosures with appropriate standards, methods, and systems.
        </Paragraph>
        <Spacer h={{ sp: 50, pc: 80 }} />
        <BHeading>1. Standards for Information Disclosure</BHeading>
        <Paragraph>
          Our company will consistently disclose information in accordance with the provisions of the Companies Act, the Financial 
          Instruments and Exchange Act, other relevant laws and regulations, as well as the &quot;Timely Disclosure Rules&quot; stipulated 
          by the Tokyo Stock Exchange.<br/>
          In the event of material decisions, occurrences, or financial information that could significantly impact investment decisions, 
          we will promptly disclose such information in accordance with the standards of the Timely Disclosure Rules. Additionally, 
          even in cases not covered by the Timely Disclosure Rules, we will proactively disclose information that we deem important or 
          beneficial for stakeholders to understand our company, enhancing fairness in information access.<br/>
          It should be noted that information that could violate personal privacy, customer information, or infringe upon the rights 
          of stakeholders will not be disclosed.
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>2. Methods of Information Disclosure</BHeading>
        <Paragraph>
          For information falling under the Timely Disclosure Rules, we will register and publicly release it on the &quot;TDnet (Timely 
          Disclosure Information Transmission System)&quot; provided by the Tokyo Stock Exchange. Subsequently, we will promptly publish 
          the information on our company website.<br/>
          For information not covered by the Timely Disclosure Rules, we will also disclose it widely through means such as 
          publication on our website.
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>3. Prevention of Insider Trading</BHeading>
        <Paragraph>
          Our company has established internal regulations to appropriately manage sensitive information and prevent insider trading, 
          fostering widespread awareness and understanding among all employees in the group.
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>4. Handling of Performance Forecasts and Future Information</BHeading>
        <Paragraph>
          Descriptions of performance forecasts, strategies, policies, goals, etc., disclosed by our company that are not historical 
          facts constitute forward-looking statements regarding future outlook. These statements are based on plans, expectations, 
          and judgments relying on information available to the company at that time and deemed reasonable under certain assumptions. 
          Actual performance may vary significantly due to various risks and uncertainties.
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>5. Quiet Period</BHeading>
        <Paragraph>
          To ensure fairness to all our stakeholders, our company observes the quiet period starting one month prior to each 
          quarterly earnings announcement.<br/>
          During this period, we refrain from answering questions or making comments regarding our 
          financial results or earnings forecasts. However, if we anticipate a significant deviation from our 
          previously announced earnings forecast during the quiet period, we will make an appropriate disclosure in 
          accordance with timely disclosure rules.
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>6. Establishment of Internal Structure</BHeading>
        <Paragraph>
          Our company ensures the effective operation of the Disclosure Policy and establishes a framework to execute timely and 
          appropriate information disclosure based on the Timely Disclosure Rules.<br/>
          We have organized an information disclosure department that collaborates with various operational departments within the 
          company. This department is responsible for proper information gathering and analysis. We have clarified the process from 
          the occurrence of information to disclosure, ensuring that the company makes disclosures based on sound judgment.
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 30 }} />
        <Image
          src="/images/TRIAL_Disclosure_IR_Policy_EN.png"
          alt="適時開示体制"
          width="100%"
        />
        <Spacer h={{ sp: 80, pc: 100 }} />
        <AHeading>IR Policy</AHeading>
        <Paragraph>
          Our company, through the combination of distribution and IT, undertakes the mission of contributing to the elimination 
          of waste, non-value-added activities, and inefficiencies in the retail industry while being helpful to our customers. 
          Our Investor Relations (IR) activities aim to execute prompt, accurate, and fair disclosure and constructive dialogue 
          with stakeholders, including shareholders and investors, under the following principles.<br/>
          To enhance our corporate value, it is crucial for stakeholders to have a correct understanding of our company. Therefore, 
          we adhere to the Disclosure Policy and disclose information regarding our management philosophy, mission and role, 
          management policies, business activities, performance, financial status, various management indicators, shareholder return 
          policies and achievements, medium-term management plans, and future prospects, ensuring accurate and timely disclosure.<br/>
          Dialogue with stakeholders such as shareholders and investors is facilitated by the IR department and the management teams 
          of various departments. To enhance the effectiveness of these dialogues, the IR department takes the lead, closely collaborating 
          with corporate departments such as finance and accounting, general affairs, and others. In order to create a basis for 
          constructive dialogue, the following initiatives are undertaken:
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BulletedList>
          <li>Conducting financial results briefings, company presentations, and facility tours</li>
          <li>Responding to inquiries from institutional investors and individual interviews</li>
          <li>Publishing materials from financial results and company briefings on the website</li>
        </BulletedList>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <Paragraph>
          Knowledge gained through communication with stakeholders is regularly reported to the management to inform business decisions.<br/>
          Through IR activities, we aim to deepen the understanding of our company among stakeholders and improve our corporate and brand value.
        </Paragraph>
      </section>
    </Layout>
  )
}

DisclosurePage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'Home', path: '/en/' },
    { name: 'Investor Relations', path: '/en/ir/' },
    { name: 'Management Policy', path: '/en/ir/manage' },
    { name: 'Disclosure / IR Policy', path: '' },
  ]

  return (
    <>
      <Head
        title="Disclosure / IR Policy"
        description="The basic policy regarding IR activities."
        ogp={{ url: 'en/ir/manage/disclosure/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle=""
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}

const Image = styled.img`
  height: 100%;
`

